import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiGet, apiPost } from "@/lib/apiClient";
import type { FinanceVendor } from "@/lib/financeTypes";
import { FinanceErrorBanner } from "@/lib/financeUi";
import { Plus } from "lucide-react";

type VendorsResponse = { vendors?: FinanceVendor[] };

export function FinanceVendorsPage() {
  const [rows, setRows] = useState<FinanceVendor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    const res = await apiGet<VendorsResponse>("/api/finance/vendors");
    if (!res.ok) {
      setError(res.error || "Could not load vendors.");
      setRows([]);
    } else {
      setRows(res.data?.vendors ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onCreate = async () => {
    setSaving(true);
    setError(null);
    const res = await apiPost<FinanceVendor>("/api/finance/vendors", {
      name: name.trim(),
      email: email.trim() || null,
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed.");
      return;
    }
    setDialogOpen(false);
    setName("");
    setEmail("");
    await load();
  };

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">Vendors</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Supplier directory for purchases (Phase 1: list and create).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add vendor
        </button>
      </div>

      {error ? <FinanceErrorBanner message={error} /> : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
            <tr>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Phone</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                  Loading vendors…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                  No vendors yet.
                </td>
              </tr>
            ) : (
              rows.map((v) => (
                <tr key={v.id} className="border-b border-slate-100 dark:border-white/5">
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{v.name?.trim() || v.id}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{v.email?.trim() || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{v.phone?.trim() || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New vendor</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Supplier LLC"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ap@example.com"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDialogOpen(false)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onCreate()}
              disabled={saving || !name.trim()}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
